import { z } from "zod";
import { doctorNoticeBodySchema } from "./doctor-notice";
import { webdocControlSchema } from "./prescription-choice";
import { prescriptionOrderSchema } from "./prescription-cycle";
import type { TreatmentState } from "./treatment";
import type { TreatmentStartState } from "./treatment-start";

// TES-1291 (TES-1267 §4, A-02/09C §4) — the same decision workspace for every
// decision kind:
//   "Samma adminflöde används vid första slutliga beslutet, beslut efter
//    komplettering, medicinsk månadsuppföljning, receptförnyelse,
//    dosjustering, läkemedelsbyte, behandlingsuppehåll och avslut."
//
// The first decision's outcomes (approve / completion / decline) live in
// treatment-decision.ts. This file adds the outcomes of a treatment that is
// already running: a pause, its resumption, the end of the treatment, and a
// medication switch. Before it there was no doctor UI for any of them;
// `TreatmentPause` was only ever written by transitions nobody could trigger
// from admin, and a switch was an order edit inside the monthly review.

// ---- The status chain ------------------------------------------------------

// Emelie's five steps (TES-1267 §4), verbatim, in order.
export const TREATMENT_STATUS_CHAIN_STEPS = [
  "planned",
  "decision",
  "prescription",
  "awaiting_subscription",
  "subscription_activated",
] as const;
export type TreatmentStatusChainStep =
  (typeof TREATMENT_STATUS_CHAIN_STEPS)[number];

export const TREATMENT_STATUS_CHAIN_LABEL: Record<
  TreatmentStatusChainStep,
  string
> = {
  planned: "Planerad behandling",
  decision: "Medicinskt behandlingsbeslut",
  prescription: "Recept utfärdat i Webdoc",
  awaiting_subscription: "Inväntar abonnemangsaktivering",
  subscription_activated: "Abonnemang aktiverat",
};

export type TreatmentStatusChainStatus = "done" | "current" | "upcoming";

export interface TreatmentStatusChainEntry {
  step: TreatmentStatusChainStep;
  label: string;
  status: TreatmentStatusChainStatus;
}

const PRESCRIPTION_REGISTERED: readonly TreatmentStartState[] = [
  "PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION",
  "PRESCRIPTION_READY",
  "TREATMENT_START_READY",
];
const SUBSCRIPTION_ACTIVE: readonly TreatmentStartState[] = [
  "SUBSCRIPTION_ACTIVATED_AWAITING_PRESCRIPTION",
  "PRESCRIPTION_READY",
  "TREATMENT_START_READY",
];

/**
 * Where the patient is in the chain, read only from what is already stored:
 * the planned medication (TES-1185) and the shared treatment-start state
 * (TES-1069/1150), which in turn reads the published decision, the
 * prescription registration and the verified subscription. Nothing new is
 * derived here, so the strip can never disagree with the gates that act on
 * the same facts.
 *
 * Each step is done on its own fact; the first step that is not done is the
 * current one. So a patient whose subscription was verified before the
 * prescription was registered shows "Recept utfärdat i Webdoc" as current
 * with the two subscription steps already done, rather than a chain that
 * pretends the order was followed.
 */
export function treatmentStatusChain(input: {
  startState: TreatmentStartState | null | undefined;
  plannedMedication: boolean;
}): TreatmentStatusChainEntry[] {
  const s = input.startState ?? null;
  const decided = s !== null && s !== "NO_POSITIVE_DECISION";
  const activated = s !== null && SUBSCRIPTION_ACTIVE.includes(s);
  const done: Record<TreatmentStatusChainStep, boolean> = {
    // A published decision carries its plan, so it counts as planned too.
    planned: input.plannedMedication || decided,
    decision: decided,
    prescription: s !== null && PRESCRIPTION_REGISTERED.includes(s),
    awaiting_subscription: activated,
    subscription_activated: activated,
  };
  const current = TREATMENT_STATUS_CHAIN_STEPS.find((step) => !done[step]);
  return TREATMENT_STATUS_CHAIN_STEPS.map((step) => ({
    step,
    label: TREATMENT_STATUS_CHAIN_LABEL[step],
    status: done[step] ? "done" : step === current ? "current" : "upcoming",
  }));
}

// ---- Pause, resume, end, switch --------------------------------------------

export const TREATMENT_STATUS_OUTCOMES = [
  "pause",
  "resume",
  "end",
  "switch",
] as const;
export const treatmentStatusOutcomeSchema = z.enum(TREATMENT_STATUS_OUTCOMES);
export type TreatmentStatusOutcome = z.infer<
  typeof treatmentStatusOutcomeSchema
>;

// "behandlingsuppehåll", "avslut" and "läkemedelsbyte" are A-02's words; the
// resumption has none of its own, so its label is ours.
export const TREATMENT_STATUS_OUTCOME_LABEL: Record<
  TreatmentStatusOutcome,
  string
> = {
  pause: "Behandlingsuppehåll",
  resume: "Återuppta behandlingen",
  end: "Avsluta behandlingen",
  switch: "Läkemedelsbyte",
};

// TES-1554 (Ö-862, Emelie 2026-10-22) — why the drug treatment ends. Only
// asked while the tenant flag `unsuitableEndRefundTask` is on; with it off an
// end carries no reason, as before. TES-1555's outcome model puts the reason
// on the drug-treatment end, not on the care relationship: ending the drug
// treatment never ends her medical case (Ö-411).
//
//   not_suitable — "Ett särskilt uttryckligt läkarutfall ska utlösa den
//                  ekonomiska hanteringen". The ONLY reason with a financial
//                  effect: the binding and notice periods are waived from the
//                  end date and admin gets one task to end the subscription
//                  and refund the paid days after it (Ö-861, Ö-863). The
//                  label is Emelie's text; Kirill confirms the medical
//                  wording (Ö-862) before the flag goes on in prod.
//   other        — every other end. Nothing financial happens.
export const TREATMENT_END_REASONS = ["not_suitable", "other"] as const;
export const treatmentEndReasonSchema = z.enum(TREATMENT_END_REASONS);
export type TreatmentEndReason = z.infer<typeof treatmentEndReasonSchema>;

export const TREATMENT_END_REASON_LABEL: Record<TreatmentEndReason, string> = {
  not_suitable:
    "Läkemedelsbehandlingen avslutas – patienten bedöms inte längre lämplig för läkemedelsbehandling",
  other: "Annan anledning",
};

/**
 * The outcomes a treatment in this state can take. The Treatment and
 * PrescriptionCycle transition tables already allow each one; this only
 * picks the ones that apply:
 *   - active          → pause (its active cycle pauses with it), a
 *                       medication switch on the prescription period she is
 *                       on (no transition at all), or end;
 *   - between_cycles  → end (there is no active cycle to pause);
 *   - paused          → resume or end.
 * Onboarding has the first decision's own outcomes, and an ended treatment
 * none.
 */
export function treatmentStatusOutcomesFor(
  state: TreatmentState | string | null | undefined,
): TreatmentStatusOutcome[] {
  switch (state) {
    case "active":
      return ["pause", "switch", "end"];
    case "between_cycles":
      return ["end"];
    case "paused":
      return ["resume", "end"];
    default:
      return [];
  }
}

/**
 * The publish. Same shape as the first decision's: the outcome, the
 * doctor's text to the patient (published as her notice under Din vård) and
 * the Webdoc confirmation. The journal tick is checked by the API (400
 * journal_not_in_webdoc) so the refusal carries its own messageKey, as on
 * the first decision.
 *
 * The text is required: a pause or an end she is not told about is the
 * thing this flow exists to prevent. TES-1290 (S3) renames the field
 * "Läkarens kommentar till behandlingsbeslutet" and prefills it per outcome.
 *
 * A switch carries the new order: product, then dose, then interval, picked
 * from the medication catalogue (TES-1292) under the same rules as every
 * other order (`prescriptionOrderSchema`, `resolveCatalogueLink`). While the
 * product's catalogue version is not in force (`catalogueVersionInForce`),
 * its dose and interval are free text, as on every other surface. The order
 * belongs to a switch only: on any other outcome it is refused rather than
 * ignored, so a client that believes it changed the medication is told it
 * did not.
 */
export const publishTreatmentStatusDecisionSchema = z
  .object({
    outcome: treatmentStatusOutcomeSchema,
    patientSummary: doctorNoticeBodySchema.pipe(
      z.string().min(1, "patient_summary_required"),
    ),
    webdocControl: webdocControlSchema,
    order: prescriptionOrderSchema.optional(),
    // TES-1554 — an end's reason. The API requires it while the tenant flag
    // is on and refuses it while the flag is off, so the reason with a
    // financial effect cannot be recorded before Ö-860 and Ö-862 are in.
    endReason: treatmentEndReasonSchema.optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.outcome !== "end" && v.endReason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endReason"],
        message: "end_reason_only_with_end",
      });
    }
    if (v.outcome === "switch" && !v.order) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["order"],
        message: "switch_order_required",
      });
    }
    if (v.outcome !== "switch" && v.order) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["order"],
        message: "order_only_with_switch",
      });
    }
  });
export type PublishTreatmentStatusDecision = z.infer<
  typeof publishTreatmentStatusDecisionSchema
>;

export const publishTreatmentStatusDecisionResultSchema = z.object({
  outcome: treatmentStatusOutcomeSchema,
  treatmentState: z.string(),
  patientNoticeId: z.string().uuid(),
});
export type PublishTreatmentStatusDecisionResult = z.infer<
  typeof publishTreatmentStatusDecisionResultSchema
>;

/**
 * Is `next` a different medication from `current`? A switch names another
 * product; the same product at another dose or interval is a dose change,
 * decided in the monthly review. Compared by name, ignoring case and
 * spacing, since an order typed before the catalogue has no version
 * reference. A current order without a medication (a cycle from before
 * TES-636) counts as different: there is nothing to compare against.
 */
export function isMedicationSwitch(
  current: string | null | undefined,
  next: string,
): boolean {
  const norm = (s: string | null | undefined) =>
    (s ?? "").trim().replace(/\s+/g, " ").toLowerCase();
  return norm(next) !== "" && norm(current) !== norm(next);
}
